import { SUPPORTED_CURRENCIES, atLeastPlan, resolveCurrency } from "@bridewell/shared";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Link2 } from "lucide-react";
import { useRef, useState, type FormEvent } from "react";
import { ApiError, http, meQuery, templatesQuery, useCreateWedding } from "@bridewell/api-client";
import { PageHeader } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox, Field, Input, MoneyInput, Select, Switch } from "@/components/ui/field";
import { UpgradePrompt } from "@/components/ui/misc";
import { JoinWeddingButton } from "@/features/people/join-wedding";
import { useMutationFeedback } from "@/features/common/use-wedding";
import { t } from "@/i18n";
import { fromDateInput, toLocalDateInput } from "@/lib/utils";

export const Route = createFileRoute("/app/new")({ component: NewWeddingPage });

/** Best guess from the browser's region; USD when the locale carries none. Every value is in SUPPORTED_CURRENCIES. */
function defaultCurrency(): string {
  let region: string | undefined;
  try { region = new Intl.Locale(navigator.language).region; } catch { /* malformed language tag */ }
  const byRegion: Record<string, string> = { NO: "NOK", SE: "SEK", DK: "DKK", GB: "GBP", TR: "TRY", CH: "CHF", CA: "CAD", AU: "AUD", JP: "JPY", IN: "INR", BR: "BRL" };
  if (region && byRegion[region]) return byRegion[region]!;
  if (["DE", "FR", "ES", "IT", "NL", "PT", "AT", "BE", "FI", "IE"].includes(region ?? "")) return "EUR";
  return "USD";
}

/** Six months out, as a local calendar date (not the UTC date, which can be yesterday or tomorrow). */
function defaultDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 180);
  return toLocalDateInput(d.toISOString());
}

/**
 * The iOS six-page wizard, as one form (the Mac app made the same call). An ordinary page inside the app shell: same
 * header and spacing as every other page, the form in a card, and joining someone else's wedding beside it.
 */
function NewWeddingPage() {
  const navigate = useNavigate();
  const fb = useMutationFeedback();
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [date, setDate] = useState(defaultDate);
  const [venue, setVenue] = useState("");
  const [currency, setCurrency] = useState(defaultCurrency);
  const [budget, setBudget] = useState(0);
  const [seed, setSeed] = useState(true);
  const me = useQuery(meQuery);
  const pro = me.data ? atLeastPlan(me.data.plan, "pro") : false;
  const templates = useQuery({ ...templatesQuery, enabled: pro });
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const setup = useRef<{ weddingId: string; remaining: string[]; uncertain: boolean; skipped: number } | null>(null);
  const [createdWeddingId, setCreatedWeddingId] = useState<string | null>(null);
  const [templateFailure, setTemplateFailure] = useState<"retryable" | "uncertain" | "skipped" | null>(null);

  const create = useCreateWedding();
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busyRef.current || setup.current?.uncertain) return;
    busyRef.current = true;
    setBusy(true);
    setTemplateFailure(null);
    try {
      if (!setup.current) {
        const w = await create.mutateAsync({
          partnerOneName: p1, partnerTwoName: p2, weddingDate: fromDateInput(date) ?? "", venueName: venue,
          currencyCode: resolveCurrency(currency), budgetTotal: budget, seedDefaults: seed,
        });
        setup.current = { weddingId: w.id, remaining: [...picked], uncertain: false, skipped: 0 };
        setCreatedWeddingId(w.id);
      }
      const progress = setup.current;
      while (progress.remaining.length > 0) {
        const id = progress.remaining[0]!;
        try {
          const result = await http.post(`/weddings/${progress.weddingId}/templates/${id}/apply`);
          progress.skipped += typeof result.meta?.skipped === "number" ? result.meta.skipped : 0;
          progress.remaining.shift();
        } catch (error) {
          // Apply is not idempotent server-side: a lost/5xx response may follow committed inserts.
          // Only a definite pre-write refusal is safe to retry; never replay acknowledged templates.
          progress.uncertain = !(error instanceof ApiError && (
            (error.status === 403 && (error.code === "FORBIDDEN" || error.isPlanRequired || error.isPlanLimit))
            || (error.status === 429 && error.code === "RATE_LIMITED")
          ));
          setTemplateFailure(progress.uncertain ? "uncertain" : "retryable");
          fb.onError(error);
          return;
        }
      }
      if (progress.skipped > 0) {
        setTemplateFailure("skipped");
        return;
      }
      await navigate({ to: "/app/w/$weddingId", params: { weddingId: progress.weddingId } });
    } catch (error) {
      fb.onError(error);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader title={t("onboarding.title")} subtitle={t("onboarding.subtitle")} />
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        <Card className="p-5 sm:p-6">
          <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" aria-busy={busy}>
            <fieldset disabled={busy || !!createdWeddingId} className="flex min-w-0 flex-col gap-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("auth.yourName")}><Input required maxLength={80} value={p1} onChange={(e) => setP1(e.target.value)} /></Field>
                <Field label={t("onboarding.partnerName")}><Input maxLength={80} value={p2} onChange={(e) => setP2(e.target.value)} /></Field>
              </div>
              <Field label={t("onboarding.pickDate")}><Input type="date" required value={date} onChange={(e) => setDate(e.target.value)} /></Field>
              <Field label={t("onboarding.venue")}><Input maxLength={120} value={venue} onChange={(e) => setVenue(e.target.value)} /></Field>
              <div className="grid grid-cols-[1fr_2fr] gap-3">
                <Field label={t("onboarding.currency")}>
                  <Select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                    {SUPPORTED_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </Select>
                </Field>
                <Field label={t("onboarding.budget")}><MoneyInput currency={currency} value={budget} onChange={setBudget} /></Field>
              </div>
              <Switch checked={seed} onChange={setSeed} label={t("onboarding.seed")} />
              {pro && (templates.data?.length ?? 0) > 0 && (
                <fieldset className="flex min-w-0 flex-col gap-1.5">
                  <legend className="mb-1.5 text-[13px] font-semibold text-text-secondary">{t("templates.startFrom")}</legend>
                  <div className="flex flex-col gap-2 rounded-control border border-border p-3">
                    {templates.data!.map((tpl) => (
                      <div key={tpl.id} className="flex items-center gap-2">
                        <Checkbox checked={picked.includes(tpl.id)} onChange={(v) => setPicked((p) => (v ? [...p, tpl.id] : p.filter((x) => x !== tpl.id)))} label={`${tpl.name} · ${t(`templates.kind.${tpl.kind}`)} · ${t("templates.items", { n: tpl.itemCount })}`} />
                        <span className="min-w-0 break-words text-sm">{tpl.name} · {t(`templates.kind.${tpl.kind}`)} · {t("templates.items", { n: tpl.itemCount })}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-text-tertiary">{t("templates.startFromHint")}</p>
                </fieldset>
              )}
            </fieldset>
            {templateFailure && <p role="alert" className="text-sm text-danger">{t(`onboarding.templates.${templateFailure}`)}</p>}
            {templateFailure !== "uncertain" && templateFailure !== "skipped" && (
              <Button type="submit" size="lg" loading={busy}>{createdWeddingId ? t("onboarding.retryTemplates") : t("onboarding.start")}</Button>
            )}
            {createdWeddingId && !busy && (
              <Link to="/app/w/$weddingId" params={{ weddingId: createdWeddingId }} className="text-center font-semibold text-accent">{t("onboarding.openWedding")}</Link>
            )}
          </form>
        </Card>
        <Card className="flex flex-col items-start gap-3" tint="var(--lavender)">
          <span className="grid h-10 w-10 place-items-center rounded-full" style={{ background: "color-mix(in srgb, var(--lavender) 22%, transparent)", color: "color-mix(in srgb, var(--lavender) 45%, var(--text))" }} aria-hidden><Link2 className="h-5 w-5" /></span>
          <h2 className="font-display text-lg font-semibold">{t("onboarding.join")}</h2>
          <p className="text-sm text-text-secondary">{t("onboarding.joinHint")}</p>
          <JoinWeddingButton />
        </Card>
      </div>
      <UpgradePrompt open={!!fb.limit} onClose={fb.clearLimit} feature={fb.limit?.feature ?? "weddings"} limit={fb.limit?.limit} plan={fb.limit?.plan} required={fb.limit?.required} />
    </>
  );
}
